/**
 * TOKCHAN Main Application Entry Point
 *
 * Full React application with Vite + TypeScript architecture.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.js';
import { logger } from './libs/utils/logger.js';
import { createAppContext } from './libs/bootstrap/createAppContext.js';
import AssetFallbackSystem from './components/AssetFallbackSystem.js';
import { gsap } from 'gsap';

// 🎵 BGM Sync Systems - メインバンドルに含めてwindowへのグローバル登録を保証
// これらはBGMコントロールより先に読み込まれる必要がある
import { VisualBeatEffects } from './components/VisualBeatEffects.js';
import { CharacterBeatEffects } from './components/CharacterBeatEffects.js';

// Tree-shaking回避: windowにグローバル登録を保証
if (typeof window !== 'undefined') {
    window.VisualBeatEffects = VisualBeatEffects;
    window.CharacterBeatEffects = CharacterBeatEffects;
}

// Import global styles
import '../styles/index.css';

/**
 * GSAP Global Exposure
 *
 * Note: Exposes GSAP globally (window.gsap) for components that require it:
 * - BodyAnimationSystem: Character body movement animations
 * - CameraDirectorSystem: 3D camera operations
 * - AnimationSystemBase: Timeline pool management
 * These systems use GSAP for complex timeline control, 3D interpolation,
 * and synchronized multi-element animations that Web Animations API cannot handle.
 * This is an intentional GSAP usage for the animation infrastructure.
 */
window.gsap = gsap;

// ============================================================================
// Application Configuration
// ============================================================================

const appConfig = {
    version: '2.0.0',
    environment: (import.meta.env.MODE || 'development') as 'development' | 'staging' | 'production',

    systems: {
        enabled: ['animation', 'ui', 'network', 'storage'],
        priorities: {
            core: 1,
            ui: 2,
            animation: 3,
            network: 4
        }
    },

    performance: {
        enableMetrics: true,
        enableProfiling: import.meta.env.DEV
    },

    features: {
        serviceWorker: false,
        pwa: false,
        animations: true,
        offlineMode: false
    }
};

// ============================================================================
// Type Definitions
// ============================================================================

type LegacyHTMLElement = HTMLElement & {
    scroll?: boolean;
};

type DebugApp = {
    systems?: Record<string, unknown>;
    config?: unknown;
    getInfo?: () => unknown;
    restartTokchan?: () => void;
} & Record<string, unknown>;

// ============================================================================
// Browser Compatibility Check
// ============================================================================

function checkBrowserCompatibility(): boolean {
    const requirements = {
        es6: typeof Symbol !== 'undefined',
        promises: typeof Promise !== 'undefined',
        fetch: typeof fetch !== 'undefined',
        webgl: (() => {
            try {
                const canvas = document.createElement('canvas');
                return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
            } catch {
                return false;
            }
        })(),
        localStorage: (() => {
            try {
                localStorage.setItem('__test__', 'test');
                localStorage.removeItem('__test__');
                return true;
            } catch {
                return false;
            }
        })()
    };

    const missingFeatures = Object.entries(requirements)
        .filter(([, supported]) => !supported)
        .map(([feature]) => feature);

    if (missingFeatures.length > 0) {
        logger.error('Browser compatibility check failed:', missingFeatures);
        showCompatibilityError(missingFeatures);
        return false;
    }

    logger.info('Browser compatibility check passed');
    return true;
}

function showCompatibilityError(missingFeatures: string[]): void {
    const root = document.getElementById('root');
    if (root) {
        root.innerHTML = `
            <div style="
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100vh;
                background: linear-gradient(135deg, #18181b 0%, #27272a 50%, #18181b 100%);
                font-family: 'Segoe UI', sans-serif;
                color: white;
            ">
                <div style="
                    background: rgba(39, 39, 42, 0.9);
                    padding: 40px;
                    border-radius: 12px;
                    text-align: center;
                    max-width: 500px;
                    border: 1px solid #ff00ff;
                    box-shadow: 0 0 20px rgba(255, 0, 255, 0.3);
                ">
                    <h2 style="margin: 0 0 20px; color: #ff00ff; text-shadow: 0 0 10px #ff00ff;">Browser Not Supported</h2>
                    <p style="color: #00ffff;">Your browser is missing required features:</p>
                    <ul style="text-align: left; margin: 20px 0; color: #e0e0e0;">
                        ${missingFeatures.map(f => `<li>${f}</li>`).join('')}
                    </ul>
                    <p style="color: #00ffff;">Please update your browser or use Chrome, Firefox, or Safari.</p>
                </div>
            </div>
        `;
    }
}

// ============================================================================
// Development Helpers
// ============================================================================

function setupDevelopmentHelpers(context: DebugApp | Record<string, unknown>): void {
    const hasProperty = <K extends string>(obj: unknown, key: K): obj is Record<K, unknown> =>
        typeof obj === 'object' && obj !== null && key in obj;

    Object.assign(window, {
        __TOKCHAN_DEBUG__: {
            app: context,
            logger,
            systems: hasProperty(context, 'systems') ? context.systems : undefined,
            config: hasProperty(context, 'config') ? context.config : undefined
        },
        tokchan: {
            status: () => hasProperty(context, 'getInfo') && typeof context.getInfo === 'function' ? context.getInfo() : null,
            systems: () => hasProperty(context, 'systems') && typeof context.systems === 'object' && context.systems !== null ? Object.keys(context.systems) : [],
            restart: () => {
                if (hasProperty(context, 'restartTokchan') && typeof context.restartTokchan === 'function') {
                    context.restartTokchan();
                }
            },
            clearLogs: () => { /* logger.clearMemoryLogs() */ }
        }
    });

    logger.info('Development helpers loaded');
    logger.info('Use window.__TOKCHAN_DEBUG__ for debugging');
}

// ============================================================================
// Global Error Handlers
// ============================================================================

function setupGlobalErrorHandlers(): void {
    window.addEventListener('unhandledrejection', (event) => {
        logger.error('Unhandled promise rejection:', event.reason);
        event.preventDefault();
    });

    window.addEventListener('error', (event) => {
        logger.error('Global error:', event.error);
    });

    logger.debug('Global error handlers configured');
}

// ============================================================================
// Application Initialization
// ============================================================================

async function initializeBackendSystems(): Promise<void> {
    try {
        // Create application context (initializes TokchanController, etc.)
        const context = await createAppContext({
            mode: 'dashboard',
            skipUI: true // UI is handled by React
        });

        logger.info('Backend systems initialized');
        logger.info(`Mode: ${context.mode}`);
        logger.info(`Systems: ${context.systems ? Object.keys(context.systems).join(', ') : 'none'}`);

        // Setup development helpers
        if (appConfig.environment === 'development') {
            setupDevelopmentHelpers(context as unknown as DebugApp);
        }

        // Initialize asset fallback system
        try {
            new AssetFallbackSystem({});
        } catch (error) {
            logger.warn('AssetFallbackSystem initialization failed:', error);
        }

    } catch (error) {
        logger.error('Failed to initialize backend systems:', error);
        throw error;
    }
}

// ============================================================================
// React Application Render
// ============================================================================

function renderApp(): void {
    const rootElement = document.getElementById('root');

    if (!rootElement) {
        logger.error('Root element not found');
        return;
    }

    const root = createRoot(rootElement);

    const handleAppReady = () => {
        logger.info('TOKCHAN React App is ready!');
    };

    root.render(
        React.createElement(App, { onReady: handleAppReady })
    );

    logger.info('React app rendered');
}

// ============================================================================
// Main Entry Point
// ============================================================================

async function main(): Promise<void> {
    logger.info('TOKCHAN Application Starting...');

    // Check browser compatibility
    if (!checkBrowserCompatibility()) {
        return;
    }

    // Setup global error handlers
    setupGlobalErrorHandlers();

    // Render React app first (shows loading screen)
    renderApp();

    // Initialize backend systems
    await initializeBackendSystems();

    logger.info('TOKCHAN Application Startup Complete');
}

// ============================================================================
// DOM Ready Check
// ============================================================================

function isDOMReady(): boolean {
    return document.readyState === 'complete' ||
        (document.readyState !== 'loading' && !(document.documentElement as LegacyHTMLElement).scroll);
}

function waitForDOM(): Promise<void> {
    return new Promise((resolve) => {
        if (isDOMReady()) {
            resolve();
            return;
        }

        const checkReady = () => {
            if (isDOMReady()) {
                document.removeEventListener('DOMContentLoaded', checkReady);
                window.removeEventListener('load', checkReady);
                resolve();
            }
        };

        document.addEventListener('DOMContentLoaded', checkReady);
        window.addEventListener('load', checkReady);
    });
}

// ============================================================================
// Application Bootstrap
// ============================================================================

waitForDOM().then(() => {
    main().catch((error) => {
        logger.error('Critical application error:', error);
    });
}).catch((error) => {
    logger.error('Failed to wait for DOM:', error);
});

// Export for external use
export { appConfig };
export default appConfig;
